// Core primitives on Base UI — UI/UX §3.10–3.12. All visuals are owned here; no library defaults.
import { Dialog as BaseDialog } from '@base-ui-components/react/dialog';
import { Switch as BaseSwitch } from '@base-ui-components/react/switch';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useState } from 'react';
import { ERROR_ICON } from '../icons/index.js';

export const cx = (...parts: (string | false | null | undefined)[]) =>
  parts.filter(Boolean).join(' ');

// ---------------------------------------------------------------- Button

/** §3.10 variants, plus `accent` (gold) which only the Holding Card CTA uses (§3.8). */
type ButtonVariant = 'primary' | 'secondary' | 'destructive' | 'ghost' | 'accent';

const BUTTON_STYLES: Record<ButtonVariant, string> = {
  primary:
    'bg-[var(--color-accent-primary)] text-[var(--color-on-accent)] border border-transparent',
  secondary:
    'bg-transparent text-[var(--color-accent-primary)] border border-[var(--color-accent-primary)]',
  destructive: 'bg-transparent text-[var(--color-error)] border border-[var(--color-error)]',
  ghost: 'bg-transparent text-[var(--color-text-secondary)] border border-transparent',
  accent:
    'bg-[var(--color-accent-secondary)] text-[var(--color-text-primary)] border border-transparent',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
  fullWidth?: boolean;
}

export function Button({
  variant = 'primary',
  loading = false,
  fullWidth = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled || loading}
      className={cx(
        'type-subheading inline-flex min-h-[44px] items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius-pill)] px-5 transition-opacity',
        'disabled:cursor-not-allowed disabled:opacity-50',
        BUTTON_STYLES[variant],
        fullWidth && 'w-full',
        className,
      )}
    >
      {loading ? 'One moment…' : children}
    </button>
  );
}

// ---------------------------------------------------------------- Input

export interface FieldProps {
  label?: string;
  hint?: string;
  error?: string;
}

const fieldShell =
  'w-full min-h-[48px] rounded-[var(--radius-md)] border bg-[var(--color-bg-surface)] px-4 py-3 type-body text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)]';

export function Input({
  label,
  hint,
  error,
  className,
  id,
  ...rest
}: FieldProps & InputHTMLAttributes<HTMLInputElement>) {
  const fieldId = id ?? rest.name ?? label?.toLowerCase().replace(/\s+/g, '-');
  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={fieldId} className="type-caption text-[var(--color-text-secondary)]">
          {label}
        </label>
      )}
      <input
        id={fieldId}
        {...rest}
        aria-invalid={error ? true : undefined}
        className={cx(
          fieldShell,
          error
            ? 'border-[var(--color-error)]'
            : 'border-[var(--color-accent-warm-gray)] focus:border-[var(--color-accent-primary)]',
          className,
        )}
      />
      {error ? (
        <p className="type-caption text-[var(--color-error)]">{error}</p>
      ) : hint ? (
        <p className="type-caption">{hint}</p>
      ) : null}
    </div>
  );
}

export function Textarea({
  label,
  hint,
  error,
  className,
  id,
  ...rest
}: FieldProps & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const fieldId = id ?? rest.name;
  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={fieldId} className="type-caption text-[var(--color-text-secondary)]">
          {label}
        </label>
      )}
      <textarea
        id={fieldId}
        rows={5}
        {...rest}
        className={cx(
          fieldShell,
          'resize-y',
          error
            ? 'border-[var(--color-error)]'
            : 'border-[var(--color-accent-warm-gray)] focus:border-[var(--color-accent-primary)]',
          className,
        )}
      />
      {error ? (
        <p className="type-caption text-[var(--color-error)]">{error}</p>
      ) : hint ? (
        <p className="type-caption">{hint}</p>
      ) : null}
    </div>
  );
}

/** §3.11 field styling over a native select — the most familiar picker for Grandpa Lou. */
export function Select({
  label,
  hint,
  error,
  className,
  id,
  children,
  ...rest
}: FieldProps & SelectHTMLAttributes<HTMLSelectElement>) {
  const fieldId = id ?? rest.name ?? label?.toLowerCase().replace(/\s+/g, '-');
  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={fieldId} className="type-caption text-[var(--color-text-secondary)]">
          {label}
        </label>
      )}
      <select
        id={fieldId}
        {...rest}
        aria-invalid={error ? true : undefined}
        className={cx(
          fieldShell,
          error
            ? 'border-[var(--color-error)]'
            : 'border-[var(--color-accent-warm-gray)] focus:border-[var(--color-accent-primary)]',
          className,
        )}
      >
        {children}
      </select>
      {error ? (
        <p className="type-caption text-[var(--color-error)]">{error}</p>
      ) : hint ? (
        <p className="type-caption">{hint}</p>
      ) : null}
    </div>
  );
}

/** On/off toggle (App Flow §6 notification preferences); 44px hit area per §4.2. */
export function Switch({
  label,
  checked,
  disabled,
  onCheckedChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  const labelId = useId();
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: Base UI's Switch.Root renders the control inside
    <label className="type-body flex min-h-[44px] items-center justify-between gap-3">
      <span id={labelId}>{label}</span>
      <BaseSwitch.Root
        aria-labelledby={labelId}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(next) => onCheckedChange(next)}
        className={cx(
          'relative inline-flex h-7 w-12 shrink-0 items-center rounded-[var(--radius-pill)] border p-[2px]',
          'border-[var(--color-accent-warm-gray)] bg-[var(--color-bg-surface)]',
          'data-[checked]:border-[var(--color-accent-primary)] data-[checked]:bg-[var(--color-accent-primary)]',
          'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
        )}
      >
        <BaseSwitch.Thumb className="block h-[22px] w-[22px] rounded-full bg-[var(--color-bg-elevated)] shadow-[var(--shadow-card)] data-[checked]:translate-x-5" />
      </BaseSwitch.Root>
    </label>
  );
}

// ---------------------------------------------------------------- Chip / StatusPill

export function Chip({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cx(
        'type-label inline-flex items-center rounded-[var(--radius-pill)] border border-[var(--color-accent-warm-gray)] bg-[var(--color-bg-surface)] px-3 py-1 text-[var(--color-text-secondary)]',
        className,
      )}
    >
      {children}
    </span>
  );
}

export type StatusTone = 'active' | 'warning' | 'neutral' | 'success';

const STATUS_STYLES: Record<StatusTone, string> = {
  active: 'bg-[var(--color-accent-primary)] text-[var(--color-on-accent)]',
  warning: 'bg-[var(--color-accent-secondary)] text-[var(--color-text-primary)]',
  neutral: 'bg-[var(--color-accent-warm-gray)] text-[var(--color-text-primary)]',
  success: 'bg-[var(--color-success)] text-[var(--color-on-accent)]',
};

export function StatusPill({
  tone = 'neutral',
  children,
}: { tone?: StatusTone; children: ReactNode }) {
  return (
    <span
      className={cx(
        'type-label inline-flex items-center rounded-[var(--radius-pill)] px-3 py-1',
        STATUS_STYLES[tone],
      )}
    >
      {children}
    </span>
  );
}

// ---------------------------------------------------------------- Card / Section

export function Card({
  children,
  className,
  id,
  as: Tag = 'div',
}: {
  children: ReactNode;
  className?: string;
  id?: string;
  as?: 'div' | 'article' | 'li';
}) {
  return (
    <Tag
      id={id}
      className={cx(
        'rounded-[var(--radius-md)] bg-[var(--color-bg-surface)] p-4 shadow-[var(--shadow-card)]',
        className,
      )}
    >
      {children}
    </Tag>
  );
}

export function Section({
  title,
  action,
  children,
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      {(title || action) && (
        <header className="flex items-center justify-between">
          {title && <h2 className="type-subheading">{title}</h2>}
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

// ---------------------------------------------------------------- Skeleton

export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cx('roots-skeleton h-4 w-full', className)} />;
}

export function CardSkeleton() {
  return (
    <Card className="flex flex-col gap-3">
      <Skeleton className="h-3 w-24" />
      <Skeleton className="h-5 w-3/4" />
      <Skeleton className="h-20 w-full" />
    </Card>
  );
}

/** §7.1 — skeletons while loading; after 10s the skeleton gives way to an error state. */
export function LoadingState({
  children,
  onRetry,
  timeoutMs = 10_000,
}: {
  children?: ReactNode;
  onRetry?: () => void;
  timeoutMs?: number;
}) {
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    if (timedOut) return;
    const timer = setTimeout(() => setTimedOut(true), timeoutMs);
    return () => clearTimeout(timer);
  }, [timeoutMs, timedOut]);
  if (!timedOut) return <>{children ?? <CardSkeleton />}</>;
  return (
    <Card className="flex flex-col items-center gap-3 py-8 text-center">
      <ERROR_ICON size={24} color="var(--color-error)" aria-hidden />
      <p className="type-body">This is taking longer than usual. Check your connection.</p>
      {onRetry && (
        <Button
          variant="secondary"
          onClick={() => {
            setTimedOut(false);
            onRetry();
          }}
        >
          Try again
        </Button>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- Dialog / Sheet

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
}

export function Dialog({ open, onOpenChange, title, description, children, actions }: DialogProps) {
  return (
    <BaseDialog.Root open={open} onOpenChange={onOpenChange}>
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className="fixed inset-0 z-40 bg-[var(--color-backdrop)]" />
        <BaseDialog.Popup className="fixed left-1/2 top-1/2 z-50 w-[min(420px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 rounded-[var(--radius-lg)] bg-[var(--color-bg-elevated)] p-6 shadow-[var(--shadow-elevated)]">
          <BaseDialog.Title className="type-heading">{title}</BaseDialog.Title>
          {description && <div className="type-body mt-2">{description}</div>}
          {children && <div className="mt-4">{children}</div>}
          <div className="mt-6 flex justify-end gap-2">{actions}</div>
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}

/** §3.12 — Secondary cancel + Primary (or Destructive) confirm, right-aligned. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  loading = false,
  disabled = false,
  onConfirm,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  disabled?: boolean;
  onConfirm: () => void;
  children?: ReactNode;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      actions={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'primary'}
            loading={loading}
            disabled={disabled}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
    </Dialog>
  );
}

/** Bottom sheet on mobile, centred panel from 768px — UI/UX §5. */
export function Sheet({ open, onOpenChange, title, children }: Omit<DialogProps, 'actions'>) {
  return (
    <BaseDialog.Root open={open} onOpenChange={onOpenChange}>
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className="fixed inset-0 z-40 bg-[var(--color-backdrop)]" />
        <BaseDialog.Popup className="fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto rounded-t-[var(--radius-lg)] bg-[var(--color-bg-elevated)] p-6 shadow-[var(--shadow-elevated)] md:inset-x-auto md:left-1/2 md:top-1/2 md:w-[min(560px,calc(100vw-48px))] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-[var(--radius-lg)]">
          <BaseDialog.Title className="type-heading">{title}</BaseDialog.Title>
          <div className="mt-4">{children}</div>
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}

// ---------------------------------------------------------------- Toast

export interface Toast {
  id: string;
  message: string;
  tone?: 'success' | 'error';
}

const ToastContext = createContext<{ push: (message: string, tone?: Toast['tone']) => void }>({
  push: () => undefined,
});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((message: string, tone: Toast['tone'] = 'success') => {
    const id = crypto.randomUUID();
    setToasts((t) => [...t, { id, message, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3000);
  }, []);

  const value = useMemo(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-24 z-[60] flex flex-col items-center gap-2 px-4 md:bottom-8"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cx(
              'type-body rounded-[var(--radius-pill)] px-4 py-2 text-[var(--color-on-accent)] shadow-[var(--shadow-elevated)]',
              t.tone === 'error' ? 'bg-[var(--color-error)]' : 'bg-[var(--color-success)]',
            )}
          >
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);

// ---------------------------------------------------------------- Avatar

export function Avatar({
  name,
  src,
  size = 40,
  filled = true,
}: {
  name: string;
  src?: string | null;
  size?: number;
  filled?: boolean;
}) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
  return src ? (
    <img
      src={src}
      alt={name}
      width={size}
      height={size}
      className="rounded-full object-cover"
      style={{ width: size, height: size }}
    />
  ) : (
    <span
      aria-hidden
      className={cx(
        'type-label inline-flex items-center justify-center rounded-full',
        filled
          ? 'bg-[var(--color-accent-warm-gray)] text-[var(--color-text-primary)]'
          : 'border border-[var(--color-accent-warm-gray)] text-[var(--color-text-secondary)]',
      )}
      style={{ width: size, height: size }}
    >
      {initials}
    </span>
  );
}

// ---------------------------------------------------------------- Offline banner

export function useOnline() {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

/** App Flow §7.3 — a calm, persistent banner, never a blocking dialog. */
export function OfflineBanner() {
  const online = useOnline();
  if (online) return null;
  return (
    <div className="type-caption sticky top-0 z-30 bg-[var(--color-accent-warm-gray)] px-4 py-2 text-center text-[var(--color-text-primary)]">
      You’re offline. Some features may be limited. We’ll sync when you’re back.
    </div>
  );
}
